import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { MedpaceDeletePatientModalComponent } from '@app/admin/manage/modals/delete-patient-modal.component';
import { AppbarComponent } from '@components/appbar/appbar.component';
import { MedpaceDataRedirectorComponent } from '@components/atoms/data-redirector/data-redirector.component';
import { HistoryTimelineComponent } from '@components/atoms/history-timeline/history-timeline.component';
import { MedpaceCaregiverInfoCardComponent } from '@components/molecules/forms/caregiver-info-card/caregiver-info-card.component';
import { StudySitesCardComponent } from '@components/molecules/forms/study-sites-card/study-sites-card.component';
import { GlobalContactNumbersComponent } from '@components/organisms/global-contact-numbers/global-contact-numbers.component';
import { AccessGroupEnum } from '@models/accessGroup';
import { RequestStatusNames } from '@models/request';
import { MedpaceAdditionalDetailsModalComponent } from './admin/manage/modals/additional-details-modal.component';
import { MedpaceDeleteRequestModalComponent } from './admin/manage/modals/delete-request-modal.component';
import { MedpacePatientTravelAirlineModalComponent } from './admin/manage/modals/patient-travel/patient-travel-airline-modal/patient-travel-airline-modal.component';
import { MedpacePatientTravelGroundModalComponent } from './admin/manage/modals/patient-travel/patient-travel-ground-modal/patient-travel-ground-modal.component';
import { MedpacePatientTravelLodgingModalComponent } from './admin/manage/modals/patient-travel/patient-travel-lodging-modal/patient-travel-lodging-modal.component';
import { MedpacePatientTravelRentalCarModalComponent } from './admin/manage/modals/patient-travel/patient-travel-rental-car-modal/patient-travel-rental-car-modal.component';
import { MedpacePatientTravelTrainModalComponent } from './admin/manage/modals/patient-travel/patient-travel-train-modal/patient-travel-train-modal.component';
import { MedpaceRequestErrorModalComponent } from './admin/manage/modals/request-error-modal.component';
import { PatientTravelPreferenceBaseComponent } from './base-classes/patient-travel-preference-base.component';
import { MedpacePatientTravelPreferencesModalBaseComponent } from './base-classes/patient-travel-preferences-modal-base.component';
import {
    MedpaceCardComponent,
    MedpaceCardContentDirective,
    MedpaceCardHeaderDirective,
} from './components/atoms/medpace-card/medpace-card.component';
import { CRCInviteCardComponent } from './components/molecules/forms/crc-invite-card/crc-invite-card.component';
import { MedpacePatientAccessiblityCardComponent } from './components/molecules/forms/patient-accessibility-card/accessibility-card.component';
import { MedpacePatientConsentCardComponent } from './components/molecules/forms/patient-consent-card/consent-card.component';
import { MedpaceKycCardComponent } from './components/molecules/forms/patient-kyc-card/kyc-card.component';
import { MedpaceKYCDeleteModalComponent } from './components/molecules/forms/patient-kyc-card/kyc-delete-modal.component';
import { MedpaceBankDetailsComponent } from './components/molecules/forms/patient-payment-card/bank-details/bank-details.component';
import { MedpacePatientPaymentCardComponent } from './components/molecules/forms/patient-payment-card/payment-card.component';
import { MedpaceAdditionalTravelInfoCardComponent } from './components/molecules/forms/request-additional-information-card/additional-travel-info.component';
import { MedpaceRequestDashboardCardComponent } from './components/molecules/forms/request-dashboard-card/request-dashboard.component';
import { MedpaceRequestTypeSelectCardComponent } from './components/molecules/forms/request-type-card/request-type-card.component';
import { MedpaceSiteInfoComponent } from './components/molecules/forms/site-info/site-info-card.component';
import { MedpaceStudyDetailsCardComponent } from './components/molecules/forms/study-details/study-details.component';
import { MedpaceStudyInfoCardComponent } from './components/molecules/forms/study-info/study-info-card.component';
import { TravelerRequestCaregiverComponent } from './components/molecules/forms/traveler-request-caregiver/traveler-request-caregiver.component';
import { MedpacePatientComponent } from './components/organisms/create-patient/patient.component';
import { MedpaceRequestComponent } from './components/organisms/create-request/request.component';
import { MedpaceCreateSiteComponent } from './components/organisms/create-site/site.component';
import { MedpaceStudyComponent } from './components/organisms/create-study/study.component';
import { MedpaceInputCardComponent } from './components/organisms/input-card/input-card.component';
import { SiteStatusEnum } from './enums/site-status.enum';
import { SetComponent } from './pages/set/set.component';
import { PatientCreateEditComponent } from './patient/create-edit/patient-add-edit.component';
import { MedpaceCreateEditPaymentTravelRequestComponent } from './request/create-edit-requests/create-edit-request.component';
import { ForcedLoginAuthGuard } from './services/guards/auth.guard';
import { GuidGuard } from './services/guards/guid.guard';
import { CreateEditSiteComponent } from './site/create-edit/create-edit-site.component';
import { StudyComponent } from './study/create-edit/new-edit-study.component';
import { MedpaceBulkSiteModalComponent } from './study/modals/bulk-site/site-upload-modal.component';
import { MedpaceNewSiteModalComponent } from './study/modals/new-site/new-site-modal.component';
import { CreateAccessRequestCrcComponent } from './v2/components/create-access-request-crc/create-access-request-crc.component';
import { CreateAccessRequestSSOComponent } from './v2/components/create-access-request-sso/create-access-request-sso.component';
import { entityHierarchyGuard } from './v2/guards/entity-hierarchy.guard';
import { permissionGuard } from './v2/guards/permission.guard';
import { AccessRequestPageComponent } from './v2/pages/access-request/access-request.page.component';
import { DashboardPageComponent } from './v2/pages/dashboard/dashboard.page.component';
import { MedpaceLoginComponent } from './v2/pages/login/login.component';
import { MedpaceLogoutComponent } from './v2/pages/logout/logout.component';
import { SitesPageComponent } from './v2/pages/sites/sites.page.component';
import { StudiesPageComponent } from './v2/pages/studies/studies.page.component';

const routes: Routes = [
    {
        path: '',
        pathMatch: 'full',
        component: MedpaceLoginComponent,
        canActivate: [ForcedLoginAuthGuard],
    },
    // v2
    {
        path: 'dashboard',
        children: [
            {
                path: '',
                pathMatch: 'full',
                canActivate: [permissionGuard(['dashboard.main.page.view', 'dashboard.crc.page.view'])],
                component: DashboardPageComponent,
            },
            {
                path: 'notification',
                pathMatch: 'full',
                canActivate: [permissionGuard(['dashboard.crc.page.view'])],
                data: {
                    routeStatuses: [SiteStatusEnum.PreRegistered],
                },
                loadComponent: () =>
                    import('../app/v2/components/crc-dashboard/crc-dashboard.page.component').then(
                        (c) => c.CrcDashboardPageComponent
                    ),
            },
        ],
    },
    {
        path: 'studies',
        pathMatch: 'full',
        component: StudiesPageComponent,
        canActivate: [permissionGuard(['studies.page.view'])],
    },
    {
        path: 'sites',
        pathMatch: 'full',
        component: SitesPageComponent,
        canActivate: [permissionGuard(['sites.page.view'])],
    },
    {
        path: 'patients',
        pathMatch: 'full',
        canActivate: [permissionGuard(['patients.page.view'])],
        loadComponent: () =>
            import('../app/v2/pages/patients/patients.page.component').then((c) => c.PatientsPageComponent),
    },
    {
        path: 'requests',
        children: [
            {
                path: '',
                pathMatch: 'full',
                canActivate: [permissionGuard(['requests.page.view'])],
                loadComponent: () =>
                    import('../app/v2/pages/requests/requests.page.component').then((c) => c.RequestsPageComponent),
            },
            {
                path: 'notification',
                pathMatch: 'full',
                canActivate: [permissionGuard(['requests.page.view'])],
                data: {
                    routeStatuses: [RequestStatusNames.Submitted],
                },
                loadComponent: () =>
                    import('../app/v2/pages/requests/requests.page.component').then((c) => c.RequestsPageComponent),
            },
            {
                path: 'pending-site-verification',
                pathMatch: 'full',
                canActivate: [permissionGuard(['requests.page.view'])],
                data: {
                    routeStatuses: [RequestStatusNames.PendingSiteVerification],
                },
                loadComponent: () =>
                    import('../app/v2/pages/requests/requests.page.component').then((c) => c.RequestsPageComponent),
            },
        ],
    },
    {
        path: 'manage',
        children: [
            {
                path: '',
                pathMatch: 'full',
                canActivate: [permissionGuard(['manage.page.view'])],
                loadComponent: () =>
                    import('../app/v2/pages/manage/manage.page.component').then((c) => c.ManagePageComponent),
            },
            {
                path: 'pending-access-requests',
                pathMatch: 'full',
                canActivate: [permissionGuard(['manage.page.view'])],
                loadComponent: () =>
                    import('../app/v2/pages/manage/manage.page.component').then((c) => c.ManagePageComponent),
                data: { showPendingAccessRequests: true },
            },
        ],
    },
    {
        path: 'studies/:id',
        pathMatch: 'full',
        loadComponent: () =>
            import('../app/v2/pages/study-overview/study-overview.page.component').then(
                (c) => c.StudyOverviewPageComponent
            ),
        canActivate: [permissionGuard(['study.overview.page.view', 'study.overview.crc.page.view'])],
    },
    {
        path: 'invitation-approvals',
        pathMatch: 'full',
        loadComponent: () =>
            import('./v2/pages/invitation-approvals/invitation-approvals.page.component').then(
                (c) => c.InvitationApprovalsPageComponent
            ),
        canActivate: [permissionGuard(['invitation-approvals.page.view'])],
    },

    {
        path: 'studies/:studyId/sites/:siteId/patients/:patientId/overview',
        pathMatch: 'full',
        loadComponent: () =>
            import('../app/v2/pages/patient-overview/patient-overview.page.component').then(
                (c) => c.PatientOverviewPageComponent
            ),
        canActivate: [permissionGuard(['patients.overview.page.view']), entityHierarchyGuard],
    }, ///// remove v2
    {
        path: 'studies/:studyId/sites/:siteId/patients/:patientId/edit',
        pathMatch: 'full',
        loadComponent: () =>
            import('../app/v2/pages/patient/patient.page.component').then((c) => c.PatientPageComponent),
        canActivate: [permissionGuard(['patients.edit']), entityHierarchyGuard],
    },
    {
        path: 'studies/:studyId/sites/:siteId/patients/new',
        pathMatch: 'full',
        loadComponent: () =>
            import('../app/v2/pages/patient/patient.page.component').then((c) => c.PatientPageComponent),
        canActivate: [permissionGuard(['patients.create']), entityHierarchyGuard],
    },
    {
        path: 'payment-approvals',
        pathMatch: 'full',
        loadComponent: () =>
            import('../app/v2/pages/payment-approvals/payment-approvals.page.component').then(
                (c) => c.PaymentApprovalsComponent
            ),
        canActivate: [permissionGuard(['payment-approvals.page.view'])],
    },
    {
        path: 'access-request',
        pathMatch: 'full',
        component: CreateAccessRequestCrcComponent,
        canActivate: [GuidGuard],
    },
    {
        path: 'access-request/sso',
        pathMatch: 'full',
        component: CreateAccessRequestSSOComponent,
        canActivate: [ForcedLoginAuthGuard],
    },
    {
        path: 'access-request/logged',
        pathMatch: 'full',
        component: CreateAccessRequestCrcComponent,
        canActivate: [ForcedLoginAuthGuard],
    },
    {
        path: 'actions',
        pathMatch: 'full',
        component: AccessRequestPageComponent,
    },
    {
        path: 'studies/:studyId/sites/:siteId',
        pathMatch: 'full',
        loadComponent: () =>
            import('../app/v2/pages/site-overview/site-overview.page.component').then(
                (c) => c.SiteOverviewPageComponent
            ),
        canActivate: [permissionGuard(['site.overview.page.view']), entityHierarchyGuard],
    },
    {
        path: 'sites/notification',
        pathMatch: 'full',
        component: MedpaceDataRedirectorComponent,
        canActivate: [permissionGuard(['sites.page.view'])],
        data: {
            content: {
                statusid: [SiteStatusEnum.PreRegistered],
            },
            redirectTo: '/sites',
        },
    },
    // v1
    {
        path: 'manage',
        pathMatch: 'full',
        loadComponent: () => import('../app/v2/pages/manage/manage.page.component').then((c) => c.ManagePageComponent),
        canActivate: [ForcedLoginAuthGuard],
        data: {
            accessRestriction: [
                AccessGroupEnum.Assistant,
                AccessGroupEnum.Coordinator,
                AccessGroupEnum.Lead_Coordinator,
                AccessGroupEnum.Manager,
            ],
        },
    },
    {
        path: 'account',
        pathMatch: 'full',
        loadComponent: () => import('../app/v2/pages/account/account.component').then((c) => c.AccountComponent),
        canActivate: [ForcedLoginAuthGuard],
    },
    {
        path: 'admin/newstudy',
        pathMatch: 'full',
        component: StudyComponent,
        canActivate: [permissionGuard(['study.clintrak.create'])],
    },
    {
        path: 'admin/requests/notification',
        pathMatch: 'full',
        component: MedpaceDataRedirectorComponent,
        canActivate: [ForcedLoginAuthGuard],
        data: {
            content: {
                status: ['Submitted'],
            },
            redirectTo: 'admin/requests',
            accessRestriction: [AccessGroupEnum.Manager, AccessGroupEnum.Assistant],
        },
    },
    {
        path: 'studies/:id/edit',
        pathMatch: 'full',
        component: StudyComponent,
        canActivate: [ForcedLoginAuthGuard, permissionGuard(['study.edit'])],
    },
    {
        path: 'studies/:id/newsite',
        pathMatch: 'full',
        component: CreateEditSiteComponent,
        canActivate: [ForcedLoginAuthGuard],
    },
    {
        path: 'studies/:id/sites/:siteId/patients/:patientId/newrequest',
        pathMatch: 'full',
        component: MedpaceCreateEditPaymentTravelRequestComponent,
        canActivate: [ForcedLoginAuthGuard, entityHierarchyGuard],
        data: {
            isUpdate: false,
        },
    },
    {
        path: 'studies/:id/sites/:siteId/patients/:patientId/requests/:requestId/edit',
        pathMatch: 'full',
        component: MedpaceCreateEditPaymentTravelRequestComponent,
        canActivate: [ForcedLoginAuthGuard, entityHierarchyGuard],
        data: {
            isUpdate: true,
        },
    },
    {
        path: 'sites/:siteId/edit',
        pathMatch: 'full',
        component: CreateEditSiteComponent,
        canActivate: [ForcedLoginAuthGuard],
    },

    {
        path: 'logout',
        pathMatch: 'full',
        component: MedpaceLogoutComponent,
    },
    {
        path: 'global-contact-numbers',
        pathMatch: 'full',
        component: GlobalContactNumbersComponent,
    },
    {
        path: 'set/:studyId',
        pathMatch: 'full',
        component: SetComponent,
        canActivate: [permissionGuard(['study.expense-management'])],
    },
    { path: '**', pathMatch: 'full', redirectTo: '' },
];

@NgModule({
    imports: [
        RouterModule.forRoot(routes, {
            initialNavigation: 'enabledBlocking',
            onSameUrlNavigation: 'reload',
            bindToComponentInputs: true,
            anchorScrolling: 'enabled',
        }),
    ],
    exports: [RouterModule],
})
export class AppRoutingModule {}

export const registeredComponents = [
    AppbarComponent,
    StudyComponent,
    PatientCreateEditComponent,
    MedpaceCreateEditPaymentTravelRequestComponent,
    MedpaceNewSiteModalComponent,
    MedpaceBulkSiteModalComponent,
    CreateEditSiteComponent,
    MedpaceInputCardComponent,
    MedpacePatientComponent,
    MedpaceStudyComponent,
    MedpaceCreateSiteComponent,
    MedpaceRequestComponent,
    MedpaceStudyInfoCardComponent,
    CRCInviteCardComponent,
    MedpaceSiteInfoComponent,
    MedpaceStudyDetailsCardComponent,
    MedpacePatientConsentCardComponent,
    MedpacePatientPaymentCardComponent,
    MedpaceRequestDashboardCardComponent,
    MedpaceKycCardComponent,
    MedpaceRequestErrorModalComponent,
    MedpaceDeleteRequestModalComponent,
    MedpaceDeletePatientModalComponent,
    MedpaceKYCDeleteModalComponent,
    MedpaceAdditionalDetailsModalComponent,
    PatientTravelPreferenceBaseComponent,
    MedpacePatientAccessiblityCardComponent,
    MedpaceRequestTypeSelectCardComponent,
    MedpaceAdditionalTravelInfoCardComponent,
    MedpaceCaregiverInfoCardComponent,
    TravelerRequestCaregiverComponent,
    MedpaceCardComponent,
    MedpaceCardContentDirective,
    MedpaceCardHeaderDirective,
    StudySitesCardComponent,
    MedpacePatientTravelGroundModalComponent,
    MedpacePatientTravelRentalCarModalComponent,
    MedpacePatientTravelPreferencesModalBaseComponent,
    MedpacePatientTravelTrainModalComponent,
    MedpacePatientTravelAirlineModalComponent,
    MedpacePatientTravelLodgingModalComponent,
    MedpaceBankDetailsComponent,
    HistoryTimelineComponent,
];
